import React, { cloneElement } from 'react';
import {
    Badge as FluentBadge,
    BadgeProps as FluentBadgeProps,
    makeStyles,
    mergeClasses,
} from '@fluentui/react-components';
import type { RecordAccent } from '../cardEvents/components/accents';

const ICON_SIZE = 12;
const DOT_SIZE = '6px';

const useStyles = makeStyles({
    // Fluent's Badge is an `inline-flex` of fixed height with no white-space rule, so a
    // label that wraps spills outside the pill instead of growing it.
    root: {
        whiteSpace: 'nowrap',
        maxWidth: '100%',
    },
    // Text-only: overrides Fluent's `min-width: 20px`, which icon-only and dot-only badges
    // rely on to keep their shape. Letting the root shrink is what engages the ellipsis in a
    // flex row and keeps the badge from inflating the min-content width around it.
    shrinkable: {
        minWidth: 0,
    },
    label: {
        display: 'block',
        minWidth: 0,
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
    },
    icon: {
        flexShrink: 0,
    },
    dot: {
        width: DOT_SIZE,
        height: DOT_SIZE,
        borderRadius: '50%',
        backgroundColor: 'currentColor',
        flexShrink: 0,
    },
});

export interface BadgeProps extends Omit<
    FluentBadgeProps,
    'shape' | 'children' | 'appearance' | 'icon' | 'color' | 'iconPosition'
> {
    /** Text content displayed inside the badge. */
    text?: string;
    /** rounded (default) | circular | square */
    shape?: FluentBadgeProps['shape'];
    /**
     * Semantic color (Fluent Badge preset).
     * - `brand` — primary accent (default)
     * - `danger` — error / critical
     * - `warning` — caution
     * - `success` — positive / ok
     * - `important` — high emphasis
     * - `informative` — neutral info
     * - `severe` — strong warning
     * - `subtle` — low emphasis
     * @default 'brand'
     */
    color?: 'brand' | 'danger' | 'warning' | 'success' | 'important' | 'informative' | 'severe' | 'subtle';
    /**
     * Visual style (Fluent Badge).
     * - `filled` — solid background (default)
     * - `ghost` — transparent background, colored foreground
     * - `outline` — border, minimal fill
     * - `tint` — soft tinted background
     * @default 'filled'
     */
    appearance?: 'filled' | 'ghost' | 'outline' | 'tint';
    /** Icon before text; size is normalized to 12px. */
    icon?: FluentBadgeProps['icon'];
    /**
     * Icon position relative to text.
     * @default 'start'
     */
    iconPosition?: 'start' | 'end';
    /**
     * Background / label / border applied on top of the Fluent preset, for palettes that live
     * in the MaperTheme rather than in Fluent's semantic colors. Prefer `StatusChip`, which
     * resolves the accent from a semantic tone.
     */
    accent?: RecordAccent;
    /** Colors only the icon, leaving the chip itself untouched. */
    iconColor?: string;
    /**
     * Renders a colored dot in the icon slot. A separate prop rather than an `icon` because
     * `icon` goes through `cloneElement` with a numeric `size`, which React would forward to
     * a bare `<span>` as an invalid DOM attribute.
     */
    dot?: string;
}

const Badge: React.FC<BadgeProps> = ({
    text,
    appearance = 'filled',
    shape = 'rounded',
    icon,
    iconPosition = 'start',
    color,
    accent,
    iconColor,
    dot,
    className,
    style,
    ...rest
}) => {
    const styles = useStyles();
    const sizedIcon = icon
        ? cloneElement(icon as React.ReactElement<{ size?: number; color?: string }>, {
              size: ICON_SIZE,
              ...(iconColor ? { color: iconColor } : {}),
          })
        : undefined;
    const content = dot ? <span className={styles.dot} style={{ color: dot }} /> : sizedIcon;
    const hasText = text !== undefined && text !== '';

    return (
        <FluentBadge
            appearance={appearance}
            shape={shape}
            icon={content ? { children: content, className: styles.icon } : undefined}
            iconPosition={iconPosition === 'end' ? 'after' : 'before'}
            color={color}
            className={mergeClasses(styles.root, hasText && styles.shrinkable, className)}
            style={
                accent
                    ? {
                          backgroundColor: accent.bg,
                          // `text` when the accent defines one: `fg` is the vivid border/icon
                          // step and does not clear AA against the tint.
                          color: accent.text ?? accent.fg,
                          borderColor: accent.stroke,
                          ...style,
                      }
                    : style
            }
            {...rest}
        >
            {hasText && <span className={styles.label}>{text}</span>}
        </FluentBadge>
    );
};

export default Badge;
